import { StudentData } from '../types';

interface StudentCardProps {
  data: StudentData;
}

const dimensionLabels: Record<string, string[]> = {
  '运动参与': ['q1Participation', 'q2Resilience'],
  '技能学习': ['q3SkillKnow', 'q4SkillImprove'],
  '健康行为': ['q5HealthSafe', 'q6HealthPrep'],
  '体育品德': ['q7MoralQueue', 'q8MoralEncourage'],
};

const questionLabels: Record<string, string> = {
  q1Participation: '运动参与',
  q2Resilience: '意志力',
  q3SkillKnow: '技能认知',
  q4SkillImprove: '技能提升',
  q5HealthSafe: '健康安全',
  q6HealthPrep: '健康准备',
  q7MoralQueue: '品德守序',
  q8MoralEncourage: '品德激励',
};

function getScoreColor(score: number): string {
  if (score >= 3.5) return 'text-green-600 bg-green-50';
  if (score >= 3.0) return 'text-green-500 bg-green-50';
  if (score >= 2.5) return 'text-yellow-600 bg-yellow-50';
  if (score >= 2.0) return 'text-orange-500 bg-orange-50';
  return 'text-red-500 bg-red-50';
}

function getScoreBarColor(score: number): string {
  if (score >= 3.5) return 'bg-green-500';
  if (score >= 3.0) return 'bg-green-400';
  if (score >= 2.5) return 'bg-yellow-400';
  if (score >= 2.0) return 'bg-orange-400';
  return 'bg-red-400';
}

export function StudentCard({ data }: StudentCardProps) {
  const { student, selfEval, microReport } = data;

  const selfEvalKeys = Object.keys(questionLabels);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{student.studentName}</h2>
            <p className="text-blue-100 text-sm mt-1">
              班级: {student.className} | 组别: {student.groupId} | 学号: {student.studentId.split('_')[1]}
            </p>
          </div>
          <div className="bg-white/20 rounded-lg px-4 py-2">
            <div className="text-white text-2xl font-bold">
              {selfEval.overallEval}
            </div>
            <div className="text-blue-100 text-xs">综合星级</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-blue-500 rounded-full"></span>
            自评得分
          </h3>

          <div className="grid grid-cols-2 gap-3">
            {selfEvalKeys.map((key) => {
              const score = selfEval[key as keyof typeof selfEval] as number;
              return (
                <div key={key} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{questionLabels[key]}</span>
                      <span className={`font-semibold ${getScoreColor(score)}`}>
                        {score.toFixed(1)}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${getScoreBarColor(score)}`}
                        style={{ width: `${(score / 4) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-purple-500 rounded-full"></span>
            维度汇总
          </h3>

          <div className="grid grid-cols-4 gap-3">
            {Object.entries(dimensionLabels).map(([dimension, keys]) => {
              const scores = keys.map((k) => selfEval[k as keyof typeof selfEval] as number);
              const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
              return (
                <div
                  key={dimension}
                  className={` rounded-lg p-3 text-center ${getScoreColor(avg).replace('text-', 'bg-').replace('-600', '-100').replace('-500', '-100').replace('-400', '-100').replace('-50', '-50')}`}
                >
                  <div className={`text-lg font-bold ${getScoreColor(avg)}`}>
                    {avg.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{dimension}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-yellow-500 rounded-full"></span>
            微观评语
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {microReport || '暂无评语'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}